import React, { Suspense, useMemo } from 'react';
import { useSelector } from 'react-redux';

import useIsMobile from 'hooks/useIsMobile';
import { isNLPLoadingSelector } from 'store/reducers/nlp/nlp-reducer';
import { showingLoadingScreenSelector } from 'store/reducers/resources-steps-reducer/resources-steps-reducer';
import {
  currentStepSelector,
  isResourcesLoadingSelector,
  previousFiltersLengthSelector,
} from 'store/reducers/resources/resources-reducer';
import { ThemeProvider } from 'styled-components';

import ResourcesNLPSearch from '../ResourcesContent/ResourcesNLPSearch';
import ResourcesSubmitMobile from '../ResourcesSubmitMobile';
import ResourcesStepsOneExamples from './ResourcesStepsOne/ResourcesStepsOneExamples';
import ResourcesStepsOneLoading from './ResourcesStepsOne/ResourcesStepsOneLoading';
import ResourcesStepsOneSpotlight from './ResourcesStepsOne/ResourcesStepsOneSpotlight';
import {
  ResourcesStepsOneDescription,
  ResourcesStepsOneBox,
  ResourcesStepsOneLeftContainer,
  ResourcesStepsOneContainer,
  ResourcesStepsOneTitle,
  ResourcesStepsOneWrapper,
  ResourcesStepsOneLeftContainerMobile,
} from './ResourcesStepsOne/styled';
import { ResourcesStepsWrapper } from './styled';

const ResourcesStepsTwo = React.lazy(() => import('./ResourcesStepsTwo'));
const ResourcesStepsThree = React.lazy(() => import('./ResourcesStepsThree'));
const ResourcesStepsFour = React.lazy(() => import('./ResourcesStepsFour'));

export default function ResourcesSteps() {
  const isMobile = useIsMobile();
  const currentStep = useSelector(currentStepSelector);
  const isNlpLoading = useSelector(isNLPLoadingSelector);
  const filters = useSelector(previousFiltersLengthSelector);
  const isResourcesLoading = useSelector(isResourcesLoadingSelector);
  const showingLoadingScreen = useSelector(showingLoadingScreenSelector);

  const isLoading = useMemo<boolean>(() => {
    return isNlpLoading || (isResourcesLoading && filters > 0);
  }, [isNlpLoading, isResourcesLoading, filters]);

  const showLoadingScreen = useMemo<boolean>(() => {
    return isNlpLoading || showingLoadingScreen;
  }, [isNlpLoading, isResourcesLoading, filters, showingLoadingScreen]);

  return (
    <ThemeProvider
      theme={{
        currentStep,
        isLoading: showLoadingScreen,
        isGettingInfo: currentStep === 1 && !isLoading && !showLoadingScreen,
      }}
    >
      <ResourcesStepsWrapper>
        <ResourcesStepsOneWrapper>
          <ResourcesStepsOneContainer>
            <ResourcesStepsOneLeftContainer>
              {currentStep === 1 && !isLoading && !showLoadingScreen ? (
                <>
                  <ResourcesStepsOneLeftContainerMobile>
                    <ResourcesStepsOneBox />
                    {isMobile ? <ResourcesSubmitMobile /> : null}
                  </ResourcesStepsOneLeftContainerMobile>
                  <ResourcesStepsOneTitle>What can we help you find?</ResourcesStepsOneTitle>
                  <ResourcesStepsOneDescription>
                    Describe what materials you're looking for in everyday language – we’ll connect
                    the dots in seconds across the member companies.
                  </ResourcesStepsOneDescription>
                </>
              ) : null}
              <ResourcesNLPSearch />
              {showLoadingScreen ? <ResourcesStepsOneLoading /> : null}
            </ResourcesStepsOneLeftContainer>
            {currentStep === 1 && !isLoading && !showLoadingScreen && isMobile ? (
              <ResourcesStepsOneExamples />
            ) : null}
            {currentStep === 1 && !isLoading && !showLoadingScreen ? (
              <ResourcesStepsOneSpotlight />
            ) : null}
          </ResourcesStepsOneContainer>
          {currentStep === 1 && !isLoading && !showLoadingScreen && !isMobile ? (
            <ResourcesStepsOneExamples />
          ) : null}
        </ResourcesStepsOneWrapper>
        <Suspense fallback={<></>}>
          {currentStep === 2 && !showingLoadingScreen ? <ResourcesStepsTwo /> : null}
          {currentStep === 3 && !showingLoadingScreen ? <ResourcesStepsThree /> : null}
          {currentStep === 4 && !showingLoadingScreen ? <ResourcesStepsFour /> : null}
        </Suspense>
      </ResourcesStepsWrapper>
    </ThemeProvider>
  );
}
